import { gql } from '@apollo/client';

export const GET_NOTIFICATIONS = gql`
  query notifications($args: NotificationsInput!) {
    notifications(args: $args) {
      edges {
        cursor
        node {
          id
          title
          description
          type
          parser
          assetId
          assetName
          createdAt
          entry {
            ... on CveNotificationEntry {
              cveId
            }
            ... on ComponentNotificationEntry {
              componentName
              componentVersion
              vendor
            }
            ... on BinaryProtectionsNotificationEntry {
              filePath
            }
            ... on CertificateNotificationEntry {
              filePath
              sha256Thumbprint
            }
            ... on CredentialUserNotificationEntry {
              filePath
              username
            }
            ... on CredentialHashNotificationEntry {
              filePath
              hash
            }
            ... on PrivateKeyNotificationEntry {
              filePath
              matchHash
            }
          }
          user
          isUnread
        }
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
        totalCount
      }
    }
  }
`;

export const GET_NOTIFICATIONS_SUMMARY = gql`
  query notificationsSummary {
    analytics {
      notificationsSummary {
        status {
          all
          unread
        }
        created {
          pastDay
          past3Days
          pastWeek
          pastMonth
        }
        types {
          asset
          sbom
          vulnerability
          setting
        }
      }
    }
  }
`;

export const GET_NOTIFICATION_SETTINGS = gql`
  query notificationSettings {
    notificationSettings {
      allowUserOverrides
      emailNotificationsEnabled
      notificationTimeframe
      preferences {
        name
        type
        category
        console
        consoleEnabled
        email
        emailEnabled
        webhook
        webhookEnabled
        controls {
          __typename
          ... on VulnerabilityControl {
            severities
            exploits
          }
          ... on CertificateControl {
            minValidity
            maxValidity
          }
          ... on AssetAnalysisControl {
            assetAnalysisEvents: events
          }
          ... on SbomFindingsControl {
            findings
          }
          ... on UserManagementControl {
            userManagementEvents: events
          }
        }
      }
    }
  }
`;

export const UPDATE_NOTIFICATION_SETTINGS = gql`
  mutation updateNotificationSettings($args: UpdateNotificationSettingsInput!) {
    updateNotificationSettings(args: $args) {
      allowUserOverrides
      emailNotificationsEnabled
      notificationTimeframe
      preferences {
        name
        type
        category
        console
        consoleEnabled
        email
        emailEnabled
        webhook
        webhookEnabled
        controls {
          __typename
          ... on VulnerabilityControl {
            severities
            exploits
          }
          ... on CertificateControl {
            minValidity
            maxValidity
          }
          ... on AssetAnalysisControl {
            assetAnalysisEvents: events
          }
          ... on SbomFindingsControl {
            findings
          }
          ... on UserManagementControl {
            userManagementEvents: events
          }
        }
      }
    }
  }
`;

export const LIST_NOTIFICATION_CONFIGURATIONS = gql`
  query listNotificationConfigurations($cursor: Cursor, $filter: NotificationConfigurationsFilter, $sort: NotificationConfigurationsSort) {
    listNotificationConfigurations(args: { cursor: $cursor, filter: $filter, sort: $sort }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          name
          disabled
          silenced
          type
          channel
          intervalSeconds
          createdAt
          updatedAt
          updatedBy
          inventoryScopes {
            organization
            groupId
            assetId
          }
          eventScopes {
            event
            threshold {
              key
              value
              operator
            }
          }
          channelConfiguration {
            __typename
            ... on NotificationConfigurationWebhook {
              url
            }
            ... on NotificationConfigurationEmail {
              address
            }
            ... on NotificationConfigurationApplication {
              _empty
            }
          }
        }
      }
    }
  }
`;

export const CREATE_NOTIFICATION_CONFIGURATION = gql`
  mutation createNotificationConfiguration($args: CreateNotificationConfigurationInput!) {
    createNotificationConfiguration(args: $args) {
      id
      name
      disabled
      silenced
      type
      channel
      intervalSeconds
      updatedBy
      inventoryScopes {
        organization
        groupId
        assetId
      }
      eventScopes {
        event
        threshold {
          key
          value
          operator
        }
      }
      channelConfiguration {
        __typename
        ... on NotificationConfigurationWebhook {
          url
        }
        ... on NotificationConfigurationEmail {
          address
        }
        ... on NotificationConfigurationApplication {
          _empty
        }
      }
    }
  }
`;

export const UPDATE_NOTIFICATION_CONFIGURATION = gql`
  mutation updateNotificationConfiguration($args: UpdateNotificationConfigurationInput!) {
    updateNotificationConfiguration(args: $args) {
      id
      name
      disabled
      silenced
      type
      channel
      intervalSeconds
      updatedBy
      inventoryScopes {
        organization
        groupId
        assetId
      }
      eventScopes {
        event
        threshold {
          key
          value
          operator
        }
      }
      channelConfiguration {
        __typename
        ... on NotificationConfigurationWebhook {
          url
        }
        ... on NotificationConfigurationEmail {
          address
        }
        ... on NotificationConfigurationApplication {
          _empty
        }
      }
    }
  }
`;

export const DELETE_NOTIFICATION_CONFIGURATION = gql`
  mutation deleteNotificationConfiguration($args: DeleteNotificationConfigurationInput!) {
    deleteNotificationConfiguration(args: $args)
  }
`;

export const LIST_NOTIFICATION_LOGS = gql`
  query listNotificationLogs($cursor: Cursor, $filter: NotificationLogsFilter, $sort: NotificationLogsSort) {
    listNotificationLogs(args: { cursor: $cursor, filter: $filter, sort: $sort }) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          notificationConfigurationId
          status
          timestamp
          eventCount
          messages
        }
      }
    }
  }
`;
